/*
 * Floretta delivery check.
 * - Asks Shopify for the delivery options of a one-item cart sent to the postal code
 *   (Storefront API, no token needed), so prices always match the Local delivery zones
 *   set in the admin. The temporary cart is separate from the customer's cart.
 * - Remembers the last postal code checked, and each answer for the browser session,
 *   so other pages show the answer straight away without asking Shopify again.
 * - On a closed day (snippets/floretta-closed-dates.liquid) the "yes" answer names the
 *   next open day instead of the same-day cutoff.
 */
(() => {
  if (customElements.get("delivery-check")) return;

  const API_URL = "/api/2025-10/graphql.json";
  const CODE_KEY = "floretta:delivery-postal-code";
  const ANSWER_KEY = "floretta:delivery-answers";
  // We only deliver in British Columbia, whose postal codes start with V.
  const POSTAL_CODE = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
  // "$25.00 CAD", the way the store shows prices with their currency.
  const formatPrice = (price, currency = "CAD") =>
    `${new Intl.NumberFormat("en-CA", { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format(price)} ${currency}`;
  let instances = 0;

  const QUERY = `mutation DeliveryCheck($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        deliveryGroups(first: 5) {
          nodes { deliveryOptions { description estimatedCost { amount currencyCode } } }
        }
      }
      userErrors { message }
    }
  }`;

  const normalize = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const display = (code) => `${code.slice(0, 3)} ${code.slice(3)}`;
  // Today in Vancouver as YYYY-MM-DD, and the days after it.
  const vancouverToday = () =>
    new Intl.DateTimeFormat("en-CA", { timeZone: "America/Vancouver", year: "numeric", month: "2-digit", day: "2-digit" })
      .format(new Date());
  const addDays = (ymd, days) => new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
  const longDate = (ymd) =>
    new Date(`${ymd}T00:00:00Z`).toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
  const escapeHtml = (text) =>
    text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

  function storage(type) {
    try {
      return window[type];
    } catch {
      return null;
    }
  }

  function readAnswers() {
    try {
      return JSON.parse(storage("sessionStorage")?.getItem(ANSWER_KEY) || "{}");
    } catch {
      return {};
    }
  }

  function saveAnswer(code, answer) {
    try {
      storage("sessionStorage")?.setItem(ANSWER_KEY, JSON.stringify({ ...readAnswers(), [code]: answer }));
    } catch {}
  }

  // Resolves to { price, currency, description } for the cheapest option, or null if we don't deliver there.
  async function fetchDelivery(code, variantId) {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          input: {
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
            buyerIdentity: {
              countryCode: "CA",
              deliveryAddressPreferences: [
                {
                  deliveryAddress: {
                    address1: "Delivery check",
                    city: "Delivery check",
                    province: "BC",
                    country: "CA",
                    zip: display(code)
                  }
                }
              ]
            }
          }
        }
      })
    });
    if (!response.ok) throw new Error(`Storefront API returned ${response.status}`);

    const { data, errors } = await response.json();
    const result = data?.cartCreate;
    if (errors?.length || !result?.cart || result.userErrors.length) {
      throw new Error("Storefront API could not price delivery");
    }

    const options = result.cart.deliveryGroups.nodes.flatMap((group) => group.deliveryOptions);
    if (options.length === 0) return null;
    const cheapest = options.reduce((best, option) =>
      Number(option.estimatedCost.amount) < Number(best.estimatedCost.amount) ? option : best
    );
    return {
      price: Number(cheapest.estimatedCost.amount),
      currency: cheapest.estimatedCost.currencyCode,
      description: cheapest.description || ""
    };
  }

  class DeliveryCheck extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.phone = this.dataset.phone;
      // Theme-authored HTML (snippets/floretta-delivery-check.liquid), shown on its own line.
      this.yesNote = this.dataset.yesNote || "";
      this.closedDates = new Set((this.dataset.closedDates || "").split(",").map((d) => d.trim()).filter(Boolean));
      this.variantId = this.dataset.variantId;
      this.form = this.querySelector("form");
      this.input = this.querySelector(".delivery-check__input");
      this.button = this.form.querySelector("button");
      this.result = this.querySelector(".delivery-check__result");

      const id = `DeliveryCheck-${++instances}`;
      this.input.id = id;
      this.querySelector("label").htmlFor = id;
      this.result.id = `${id}-result`;
      this.input.setAttribute("aria-describedby", this.result.id);

      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.check(this.input.value, true);
      });
      this.input.addEventListener("input", () => this.show("", ""));

      const stored = storage("localStorage")?.getItem(CODE_KEY) || "";
      if (POSTAL_CODE.test(stored)) {
        this.input.value = display(stored);
        this.check(stored, false);
      }
    }

    async check(value, remember) {
      const code = normalize(value);
      if (!POSTAL_CODE.test(code)) {
        this.show("invalid", "Please enter the full postal code, for example V2X 1A1.");
        return;
      }

      this.input.value = display(code);
      if (remember) {
        try {
          storage("localStorage")?.setItem(CODE_KEY, code);
        } catch {}
      }

      let answer = readAnswers()[code];
      if (answer === undefined) {
        if (!code.startsWith("V")) {
          answer = null;
        } else {
          this.show("checking", "Checking…");
          this.button.disabled = true;
          try {
            answer = await fetchDelivery(code, this.variantId);
          } catch (error) {
            console.error("Delivery check:", error);
            this.show("error", `Sorry, we couldn't check right now. Please call us at ${this.phoneLink()}.`);
            return;
          } finally {
            this.button.disabled = false;
          }
        }
        saveAnswer(code, answer);
      }
      // A newer check started while this one was waiting.
      if (normalize(this.input.value) !== code) return;
      // The delivery map (floretta-delivery-map.js) highlights the customer's area.
      if (remember) document.dispatchEvent(new CustomEvent("floretta:delivery-checked", { detail: { code, answer } }));

      if (answer) {
        const details = answer.description ? ` ${escapeHtml(answer.description)}` : "";
        const yesNote = this.closedNote() || this.yesNote;
        const note = this.yesNote ? `<span class="delivery-check__note">${yesNote}</span>` : "";
        this.show(
          "yes",
          `Yes, we deliver to ${display(code)}. Delivery: <strong class="delivery-check__price">${formatPrice(answer.price, answer.currency)}</strong>.${details}${note}`
        );
      } else {
        this.show(
          "no",
          `Sorry, we don't deliver to ${display(code)}.<span class="delivery-check__note">Questions? Call us at ${this.phoneLink()}</span>`
        );
      }
    }

    // When we're closed today, replaces the same-day cutoff with the next open day.
    closedNote() {
      const today = vancouverToday();
      if (!this.closedDates.has(today)) return "";
      for (let days = 1; days <= 90; days++) {
        const day = addDays(today, days);
        if (this.closedDates.has(day)) continue;
        return `We're closed today. Next delivery: <strong>${days === 1 ? "tomorrow" : longDate(day)}</strong>.`;
      }
      return "We're closed today.";
    }

    phoneLink() {
      return `<a href="tel:${this.phone.replace(/[^\d+]/g, "")}">${this.phone}</a>`;
    }

    show(status, html) {
      if (status) this.result.dataset.status = status;
      else this.result.removeAttribute("data-status");
      this.result.innerHTML = html;
    }
  }

  customElements.define("delivery-check", DeliveryCheck);
})();
